import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const adobeWorkfront = defineConnector({
  identifier: "adobeWorkfront",
  name: "Adobe Workfront",
  tagLine: "Work management platform",
  logo,
  protocol: "http",
  tags: ["productivity"],
  defaultUrlPattern: ({ hostname }) => domainToRegex(hostname),
  auth: oauthAuth,
  steps: [
    {
      title: "Enter your Adobe Workfront instance details",
      items: [
        {
          kind: "markdown",
          content: "Enter the details for your Adobe Workfront instance.",
        },
        {
          kind: "input",
          label: "Hostname",
          placeholder: "acme.my.workfront.com",
          required: true,
          target: { kind: "configuration", field: "hostname" },
          helperText:
            "The hostname of your Adobe Workfront account (e.g. https://acme.my.workfront.com)",
        },
      ],
    },
    {
      title: "Create an Adobe Workfront app",
      items: [
        {
          kind: "markdown",
          content:
            "Open the [Adobe Workfront API basics docs](https://experienceleague.adobe.com/en/docs/workfront/using/adobe-workfront-api/api-general-information/api-basics) and create an OAuth2 application in your Workfront setup area. Copy the client ID and client secret, and note your Workfront hostname.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content: "In your app’s settings, add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/adobeWorkfront/callback",
        },
      ],
    },
    {
      title: "Enter your app credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Paste your Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Paste your Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Authorize your Adobe Workfront account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Adobe Workfront account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: (configuration) =>
      `https://${configuration.hostname}/integrations/oauth2/authorize`,
    tokenEndpoint: (configuration) =>
      `https://${configuration.hostname}/integrations/oauth2/api/v1/token`,
    tokenEndpointAuthMethod: () => "client_secret_basic",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: (configuration) => `https://${configuration.hostname}/attask/api/`,
  },
  publicEnvVars: ({ hostname }) => ({
    ADOBE_WORKFRONT_URL: hostname ? `https://${hostname}` : "",
  }),
});
